import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { loadPinnedChallengeBets } from "../utils/pinnedChallengeBets";
import {
  CHALLENGE_MODAL_SHOWN_SESSION_KEY,
  getPinnedChallengeUsers,
  PINNED_CHALLENGE_USERS_CHANGED_EVENT,
} from "../utils/pinnedChallengeUsers";

const REFRESH_INTERVAL = 8000;

const displayBetDate = (value) =>
  value ? moment(`${value}Z`).utc().local().format("DD/MM/YYYY HH:mm:ss") : "-";

const betsSignature = (bets) =>
  bets
    .map(
      (bet, index) =>
        bet.betId ||
        bet.id ||
        `${bet.userName || bet.username}-${bet.created_date}-${bet.event_name}-${bet.amount}-${index}`
    )
    .sort()
    .join("|");

export default function PinnedChallengeBetsModal() {
  const [pinnedUsers, setPinnedUsers] = useState(() => getPinnedChallengeUsers());
  const [bets, setBets] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const requestId = useRef(0);

  const refreshBets = useCallback(async () => {
    const currentRequest = ++requestId.current;
    if (!pinnedUsers.length) {
      setBets([]);
      return;
    }

    try {
      const currentBets = await loadPinnedChallengeBets(pinnedUsers);
      if (currentRequest !== requestId.current) return;

      setBets(currentBets);
      const signature = betsSignature(currentBets);
      const alreadyShown = window.sessionStorage.getItem(
        CHALLENGE_MODAL_SHOWN_SESSION_KEY
      );
      if (signature && !alreadyShown) {
        window.sessionStorage.setItem(CHALLENGE_MODAL_SHOWN_SESSION_KEY, "1");
        setIsOpen(true);
      }
    } catch {
      // A transient dashboard request must not interrupt the rest of the app.
    }
  }, [pinnedUsers]);

  useEffect(() => {
    refreshBets();
    const refresh = window.setInterval(refreshBets, REFRESH_INTERVAL);
    return () => window.clearInterval(refresh);
  }, [refreshBets]);

  useEffect(() => {
    const syncPinnedUsers = () => setPinnedUsers(getPinnedChallengeUsers());
    window.addEventListener("storage", syncPinnedUsers);
    window.addEventListener(PINNED_CHALLENGE_USERS_CHANGED_EVENT, syncPinnedUsers);
    return () => {
      window.removeEventListener("storage", syncPinnedUsers);
      window.removeEventListener(PINNED_CHALLENGE_USERS_CHANGED_EVENT, syncPinnedUsers);
    };
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Pinned Challengers — Current Bets</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} className="current-bets-page">
          <div className="table-responsive data-table">
            <table className="table table-bordered datatable dataTable current-bets-table">
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Event Name</th>
                  <th>User Name</th>
                  <th>M Name</th>
                  <th>Nation</th>
                  <th className="current-bets-number">User Rate</th>
                  <th className="current-bets-number">Amount</th>
                  <th>Place Date</th>
                </tr>
              </thead>
              <tbody>
                {bets.map((bet, index) => (
                  <tr
                    key={bet.betId || bet.id || `${bet.created_date}-${index}`}
                    className={bet.betSide === "BACK" ? "back-bet-row" : "lay-bet-row"}
                  >
                    <td>{bet.event_type || "-"}</td>
                    <td>{bet.event_name || "-"}</td>
                    <td>{bet.userName || bet.username || "-"}</td>
                    <td>{bet.marketName || "-"}</td>
                    <td>{bet.runner_name || "-"}</td>
                    <td className="current-bets-number">{bet.userRate}</td>
                    <td className="current-bets-number">{bet.amount}</td>
                    <td>{displayBetDate(bet.created_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
